import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Scissors, Eye, EyeOff } from 'lucide-react';
import { useSubdomainBranding } from '../hooks/useSubdomainBranding';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  // Pre-auth branding from the subdomain host (e.g. harryrosen.tailorflow.tech).
  // Falls back to default TailorFlow brand on the apex domain / preview /
  // localhost / unbranded subdomains.
  const { branding, subdomain } = useSubdomainBranding();
  const accent = branding?.primary_color || '#C2A878';
  const buttonColor = branding?.button_color || accent;
  const shopName = branding?.store_name || 'TailorFlow AI';
  const tagline = branding ? `Virtual Fitting · ${branding.store_name}` : 'Virtual Fitting for Custom Clothiers';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1721265250100-b6d3e43bdcda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB0YWlsb3JpbmclMjBzdHVkaW8lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU5MzU4MjN8MA&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0A0A0C]/90"></div>
      
      <div className="relative z-10 w-full max-w-md px-6 fade-in">
        {/* Logo */}
        <div className="text-center mb-12">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={shopName}
              className="h-14 mx-auto mb-6 object-contain"
              data-testid="login-branded-logo"
              style={{ filter: 'drop-shadow(0 0 12px rgba(0,0,0,0.6))' }} />
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 border mb-6"
              style={{ borderColor: accent }}>
              <Scissors className="w-8 h-8" style={{ color: accent }} />
            </div>
          )}
          <h1 className="text-4xl font-light tracking-tight text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="login-shop-name">
            {shopName}
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-2 tracking-wide">
            {tagline}
          </p>
          {subdomain && branding && (
            <p className="text-[10px] uppercase tracking-[0.25em] mt-3" style={{ color: accent }}
              data-testid="login-subdomain-pill">
              {subdomain}.tailorflow.tech
            </p>
          )}
        </div>

        {/* Login Form */}
        <div className="bg-[#121215] border border-[#27272A] p-8">
          <h2 className="text-2xl font-light text-[#F5F5F5] mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Welcome Back
          </h2>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="label-luxury">Email Address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-underline w-full bg-transparent text-[#F5F5F5] border-0 border-b border-[#27272A] focus:border-[#C2A878] rounded-none px-0"
                placeholder="your@email.com"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <Label className="label-luxury">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-underline w-full bg-transparent text-[#F5F5F5] border-0 border-b border-[#27272A] focus:border-[#C2A878] rounded-none px-0 pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#C2A878] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              style={branding ? { backgroundColor: buttonColor, color: '#0A0A0C', borderColor: buttonColor } : undefined}
              data-testid="login-submit"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-[#A1A1AA]">
              Authorized personnel only
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
